import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChefHat, AlertTriangle, Thermometer } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Loader } from "@/components/kit";
import { Emblem } from "@/components/Logo";

const OVERAGES = [0, 2, 5, 10];

export default function KitchenMode() {
  const { id } = useParams();
  const nav = useNavigate();
  const [covers, setCovers] = useState(120);
  const [overage, setOverage] = useState(5);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try { const { data } = await api.post(`/menus/${id}/kitchen`, { covers: +covers, overage: +overage }); setData(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { run(); }, []);

  return (
    <div className="min-h-screen bg-ink">
      <header className="glass sticky top-0 z-30 flex items-center justify-between px-5 lg:px-8 h-16">
        <button onClick={() => nav(`/menus/${id}`)} className="flex items-center gap-2 text-white/60 hover:text-gold"><ArrowLeft size={18} /> Back to menu</button>
        <div className="flex items-center gap-2.5"><Emblem className="h-7 w-7" /><span className="font-serif gold-text tracking-wider">KITCHEN MODE</span></div>
      </header>

      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8">
        <div className="matte-card p-6 mb-6 flex flex-wrap items-end gap-5">
          <div><label className="label-eyebrow block mb-2">Covers</label>
            <input type="number" value={covers} onChange={(e) => setCovers(e.target.value)} data-testid="kitchen-covers" className="w-32 bg-surface border border-white/10 rounded-sm px-4 py-3 text-2xl font-mono text-gold outline-none focus:border-gold/60" /></div>
          <div><label className="label-eyebrow block mb-2">Overage</label>
            <div className="flex gap-2">
              {OVERAGES.map((o) => <button key={o} onClick={() => setOverage(o)} className={`px-4 py-3 rounded-sm text-sm border min-h-[48px] ${overage === o ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-white/60"}`}>{o}%</button>)}
            </div></div>
          <button onClick={run} data-testid="kitchen-scale-btn" className="btn-gold px-8 py-3 text-sm uppercase tracking-widest rounded-sm min-h-[48px]">Scale Quantities</button>
        </div>

        {loading && <Loader label="Building production sheets…" />}
        {data && !loading && (
          <div className="space-y-5">
            <h1 className="font-serif text-4xl">{data.menu_name}</h1>
            <p className="text-white/50">{data.covers} covers · {data.overage}% overage</p>
            {data.stations.map((s, i) => (
              <div key={i} className="matte-card p-6" data-testid={`kitchen-station-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div><p className="label-eyebrow">{s.course} · {s.station}</p><h2 className="font-serif text-3xl mt-1">{s.name}</h2></div>
                  <span className="px-4 py-2 rounded-sm bg-gold/10 border border-gold/30 text-gold text-sm">{s.difficulty}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="label-eyebrow mb-3 flex items-center gap-2"><ChefHat size={14} className="text-gold" /> Scaled Quantities</p>
                    <div className="divide-y divide-white/5">
                      {s.ingredients.map((ing, j) => (
                        <div key={j} className="flex items-center justify-between py-2.5 text-lg">
                          <span className="text-white/85">{ing.name} {ing.prep && <span className="text-white/40 text-base">· {ing.prep}</span>}</span>
                          <span className="font-mono text-gold text-xl">{ing.quantity} {ing.unit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="label-eyebrow mb-3">Method</p>
                    <ol className="space-y-2.5">{s.method.map((m, j) => <li key={j} className="flex gap-3 text-lg text-white/75"><span className="font-mono text-gold">{j + 1}</span> {m}</li>)}</ol>
                    {s.plating_map?.length > 0 && <><p className="label-eyebrow mt-5 mb-2">Plating</p><ol className="space-y-1.5">{s.plating_map.map((m, j) => <li key={j} className="text-white/65 text-base">{j + 1}. {m}</li>)}</ol></>}
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-4 pt-4 border-t border-white/5">
                  <span className="flex items-center gap-2 text-red-300 text-sm"><AlertTriangle size={15} /> Allergens: {(s.allergens || []).join(", ") || "none"}</span>
                  {s.holding && <span className="flex items-center gap-2 text-white/50 text-sm"><Thermometer size={15} className="text-gold/60" /> {s.holding}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
